// eslint-disable-next-line boundaries/element-types
import { useRolodexEntries } from 'features/rolodex'
import { useMemo } from 'react'
// eslint-disable-next-line boundaries/element-types
import { RolodexEntryType } from 'types'
import { parseString } from 'utils/parsingHelpers'

export const useFilteredSalesforceAccountsForSitesUnderParent = (
  parentOrgSalesforceAccountId: string,
  currentAccountId?: string
) => {
  return useSalesforceAccounts(parentOrgSalesforceAccountId, currentAccountId)
}

export const useSalesforceAccountsForOrganization = (
  currentAccountId?: string
) => {
  return useSalesforceAccounts(undefined, currentAccountId)
}

const useSalesforceAccounts = (
  parentOrgSalesforceAccountId?: string,
  currentAccountId?: string
) => {
  const { entries: sitesOrOrgsInCantilever } = useRolodexEntries({
    type: [RolodexEntryType.SITE, RolodexEntryType.ORGANIZATION],
    tags: {
      [RolodexEntryType.SALESFORCE_ACCOUNT]: '',
    },
  })

  const { entries: allSalesforceAccounts } = useRolodexEntries({
    type: RolodexEntryType.SALESFORCE_ACCOUNT,
  })

  const salesforceAccountOptions = useMemo(() => {
    const existingAccountIds = new Set(
      sitesOrOrgsInCantilever
        .map((siteOrOrg) => siteOrOrg.tags[RolodexEntryType.SALESFORCE_ACCOUNT])
        .filter((id) => id !== currentAccountId)
        .filter(Boolean)
    )

    const filteredAccounts = allSalesforceAccounts.filter(
      (account) => !existingAccountIds.has(account.id)
    )

    const accountsToProcess = parentOrgSalesforceAccountId
      ? filteredAccounts.filter((account) => account.data.parent)
      : filteredAccounts

    const currentAccount =
      currentAccountId &&
      !accountsToProcess.some((account) => account.id === currentAccountId)
        ? allSalesforceAccounts.find(
            (account) => account.id === currentAccountId
          )
        : null

    const mappedAccounts = [
      ...accountsToProcess,
      ...(currentAccount ? [currentAccount] : []),
    ].map((account) => ({
      value: account.id,
      searchName: parseString(account.data.name) || '',
      label: parseString(account.data.name) || '',
      parent:
        typeof account.data.parent === 'string' ? account.data.parent : 'None',
    }))

    // Sort by parent if orgSalesforceAccountId is provided
    if (parentOrgSalesforceAccountId) {
      return mappedAccounts.sort((a, b) => {
        if (a.parent === parentOrgSalesforceAccountId) return -1
        if (b.parent === parentOrgSalesforceAccountId) return 1
        return a.label.localeCompare(b.label)
      })
    }

    return mappedAccounts
  }, [
    allSalesforceAccounts,
    sitesOrOrgsInCantilever,
    parentOrgSalesforceAccountId,
    currentAccountId,
  ])

  const salesforceAccountIdToNameMap = useMemo(() => {
    return new Map(
      allSalesforceAccounts.map((account) => [
        account.id,
        parseString(account.data.name) || '',
      ])
    )
  }, [allSalesforceAccounts])

  return {
    salesforceAccountOptions,
    salesforceAccountIdToNameMap,
  }
}

export const useSalesforceJobsForSite = ({
  siteSlug,
  orgSlug,
  jobYear,
  currentJobId,
}: {
  siteSlug: string
  orgSlug: string
  jobYear: string
  currentJobId?: string
}) => {
  const { sites: siteBySlug, organizations: orgBySlug } = useRolodexEntries({
    type: [RolodexEntryType.SITE, RolodexEntryType.ORGANIZATION],
    ids: [siteSlug, orgSlug],
  })
  const siteSalesforceAccountId =
    siteBySlug?.[0]?.tags[RolodexEntryType.SALESFORCE_ACCOUNT]
  const orgSalesforceAccountId =
    orgBySlug?.[0]?.tags[RolodexEntryType.SALESFORCE_ACCOUNT]

  const { entries: salesforceJobs } = useRolodexEntries({
    type: RolodexEntryType.SALESFORCE_JOB,
    tags: {
      [RolodexEntryType.SALESFORCE_ACCOUNT]: [
        siteSalesforceAccountId,
        orgSalesforceAccountId,
      ].filter(Boolean),
    },
    filters: [
      {
        field: 'data.start_date',
        op: '>=',
        value: jobYear.slice(0, 4) + '-01-01',
      },
      {
        field: 'data.start_date',
        op: '<=',
        value: jobYear.slice(0, 4) + '-12-31',
      },
    ],
  })

  const salesforceJobOptions = useMemo(() => {
    const currentJob =
      currentJobId && !salesforceJobs.some((job) => job.id === currentJobId)
        ? salesforceJobs.find((job) => job.id === currentJobId)
        : null

    const siteJobs = salesforceJobs.filter(
      (job) =>
        job.tags[RolodexEntryType.SALESFORCE_ACCOUNT] ===
        siteSalesforceAccountId
    )
    const orgJobs = salesforceJobs.filter(
      (job) =>
        job.tags[RolodexEntryType.SALESFORCE_ACCOUNT] === orgSalesforceAccountId
    )

    const mapJobToOption = (
      job: (typeof salesforceJobs)[0],
      prefix?: string
    ) => ({
      value: job.id,
      searchName: parseString(job.data.name) || '',
      label: prefix
        ? `${prefix}: ${parseString(job.data.name) || ''}`
        : parseString(job.data.name) || '',
      startDate: job.raw.data.start_date as string,
      endDate: job.raw.data.end_date as string,
    })

    return [
      ...siteJobs.map((job) => mapJobToOption(job, 'Site Level')),
      ...orgJobs.map((job) => mapJobToOption(job, 'Org Level')),
      ...(currentJob ? [mapJobToOption(currentJob)] : []),
    ]
  }, [
    salesforceJobs,
    currentJobId,
    siteSalesforceAccountId,
    orgSalesforceAccountId,
  ])

  return { salesforceJobOptions }
}
